import React, {
  useMemo, useRef, useImperativeHandle, useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Icon from '../../../../../shared/_TBComponents/Icon';
import { EXERCISE_TYPE } from '../../../../../shared/constants';
import TYPE_OPTIONS from '../exerciseTypeOptions';
import NavigatorManageTable, {
  nameSort,
  useActionMenu,
  OverflowNameList,
  ActionMenuWrapper,
  ActionMenuButton,
  ActionMenuContainer,
  ActionMenuDivider,
  ActionMenuItemButton,
  Dash,
} from './NavigatorManageTable';

// Thin wrapper over the shared NavigatorManageTable: supplies the exercise-specific
// columns, cells, bulk bar, and merge scroll-to affordance.

const EMPTY_TRACKING_LABEL = '–';
const getMoveLabel = (types = []) => {
  const progressionCount = types.filter((type) => type === EXERCISE_TYPE.Progression).length;
  if (progressionCount === 0) return 'Move to Our Exercises';
  if (progressionCount === types.length) return 'Move to Progressions';
  return 'Move to Our Exercises / Progressions';
};

// Track values have different meanings across exercise types.
const TRACK_LABELS = {
  1: 'Max Weight (1RM)',
  2: 'Highest Reps',
  3: 'Max Weight (3RM)',
  4: 'Highest Weight',
  5: 'Max Weight (5RM)',
};

const SAQC_TRACK_LABELS = {
  1: 'Higher Value is Better',
  2: 'Lower Value is Better',
};

const SAQC_UNIT_LABELS = {
  MS: 'Minutes & Seconds',
  S: 'Seconds',
  CM: 'Centimeters',
  F: 'Feet & Inches',
  I: 'Inches',
  K: 'Kilometers',
  M: 'Meters',
  L: 'Miles',
  Y: 'Yards',
  CL: 'Calories',
  KH: 'Kilometer/Hours (KPH)',
  MH: 'Miles/Hour (MPH)',
  W: 'Watts',
};

const CIRCUIT_INPUT_LABELS = {
  S: 'Sets',
  R: 'Reps',
  T: 'Time',
  C: 'Completion',
};

const getTrackingLabel = (row) => {
  const { type, track, prSortOrder } = row;
  const trackValue = Number(track);

  if (type === EXERCISE_TYPE.Lift) {
    return TRACK_LABELS[trackValue] || EMPTY_TRACKING_LABEL;
  }
  if (type === EXERCISE_TYPE.RunCardio) {
    const prSortOrderValue = Number(prSortOrder);
    const isTrackingEnabled = trackValue > 0 || prSortOrderValue > 0;
    return isTrackingEnabled
      ? SAQC_TRACK_LABELS[prSortOrderValue] || EMPTY_TRACKING_LABEL
      : EMPTY_TRACKING_LABEL;
  }
  if (type === EXERCISE_TYPE.Circuit) return trackValue > 0 ? 'Ranked on Leaderboard' : EMPTY_TRACKING_LABEL;
  if (type === EXERCISE_TYPE.Question || type === EXERCISE_TYPE.Questionnaire) {
    return EMPTY_TRACKING_LABEL;
  }
  return EMPTY_TRACKING_LABEL;
};

const getInputLabel = (row) => {
  if (row.type === EXERCISE_TYPE.RunCardio) {
    return row.inputType === 'C' ? 'Completion' : SAQC_UNIT_LABELS[row.unit] || null;
  }
  if (row.type === EXERCISE_TYPE.Circuit) return CIRCUIT_INPUT_LABELS[row.inputType] || null;
  return null;
};

const getLimitLabel = (row) => {
  if (row.type !== EXERCISE_TYPE.Circuit) return null;
  const detail = String(row.detail ?? '').trim();
  if (!detail) return null;
  if (row.detailType === 'sets' || row.detailType === 'reps') {
    const unit = row.detailType === 'sets' ? 'set' : 'rep';
    return `${detail} ${parseInt(detail, 10) === 1 ? unit : `${unit}s`}`;
  }
  return detail;
};

// type value -> label (e.g. 'L' -> 'Lifts'), for the Type column.
const TYPE_LABELS = {
  ...Object.fromEntries(
    TYPE_OPTIONS.filter((o) => o.value !== 'all').map((o) => [o.value, o.label]),
  ),
  [EXERCISE_TYPE.Progression]: 'Progressions',
};

// defaultOptions boolean flags -> per-type "Attributes" chips.
const ATTRIBUTE_DEFS = [
  { key: 'eachSide', label: 'Each Side' },
  { key: 'barSpeed', label: 'Velocity' },
  { key: 'peakPower', label: 'Peak Power' },
  { key: 'trackVolLoad', label: 'Track Volume Load' },
  { key: 'trackRepCount', label: 'Track Rep Count' },
  { key: 'liftRepsOnly', label: 'Bodyweight' },
  { key: 'liftComp', label: 'Completion' },
  { key: 'liftCoachComp', label: 'Coach Comp' },
  { key: 'cardioComp', label: 'Completion' },
  { key: 'cardioCoach', label: 'Coach Comp' },
  { key: 'circuitCoach', label: 'Coach Comp' },
];

const ExerciseNameText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
`;

const LinkedText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${(props) => (props.unresolved ? 'color: #9E9E9E;' : '')}
`;

const NameCell = ({ row }) => {
  const { name } = row.original;
  return <ExerciseNameText title={name}>{name || 'Untitled exercise'}</ExerciseNameText>;
};

NameCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.shape({ name: PropTypes.string }) }).isRequired,
};

const TypeCell = ({ row }) => {
  const label = TYPE_LABELS[row.original.type];
  return label ? <span>{label}</span> : <Dash />;
};

TypeCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.shape({ type: PropTypes.string }) }).isRequired,
};

const TrackingCell = ({ row }) => {
  const label = getTrackingLabel(row.original);
  return label === EMPTY_TRACKING_LABEL ? <Dash /> : <span>{label}</span>;
};

TrackingCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      type: PropTypes.string,
      track: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      prSortOrder: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  }).isRequired,
};

// Linked-exercise display, e.g. "Back Squat (85%)"; name is resolved client-side by
// ManageExercises since the API only returns the id. Id/name, not %, is the source of truth.
const getLinkedInfo = (e) => {
  if (!e.linkedMaxExerciseId && !e.linkedMaxExerciseName) return null;
  const pct = Number(e.linkedMaxPercentage);
  const suffix = pct ? ` (${pct}%)` : '';
  const name = e.linkedMaxExerciseName && String(e.linkedMaxExerciseName).trim();
  return name
    ? { label: `${name}${suffix}`, resolved: true }
    : { label: `Linked max${suffix}`, resolved: false };
};

const LinkedExerciseCell = ({ row }) => {
  const info = getLinkedInfo(row.original);
  if (!info) return <Dash />;
  return (
    <LinkedText title={info.label} unresolved={!info.resolved}>
      {info.label}
    </LinkedText>
  );
};

LinkedExerciseCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      linkedMaxExerciseId: PropTypes.number,
      linkedMaxExerciseName: PropTypes.string,
      linkedMaxPercentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  }).isRequired,
};

const TagsCell = ({ row }) => {
  const { tags, id } = row.original;
  return (
    <OverflowNameList
      items={tags}
      idPrefix='exercise-tags'
      rowId={id}
      variant='pill'
      maxVisible={(tags || []).length}
      adaptiveWrap={false}
    />
  );
};

TagsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number,
      tags: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    }),
  }).isRequired,
};

const AttributesCell = ({ row }) => {
  const e = row.original;
  const opts = e.defaultOptions || {};
  const active = ATTRIBUTE_DEFS.filter((a) => opts[a.key]
    && !(a.key === 'cardioComp' && e.type === EXERCISE_TYPE.RunCardio && e.inputType === 'C'));
  const hasVideo = !!e.videoUrl;
  const handleVideoClick = (ev) => {
    ev.stopPropagation();
    e.onPlayVideo(e.videoUrl);
  };
  const inputLabel = getInputLabel(e);
  const limitLabel = getLimitLabel(e);
  const attributeItems = [
    inputLabel ? { id: 'input', name: inputLabel } : null,
    limitLabel ? { id: 'limit', name: limitLabel } : null,
    hasVideo ? { id: 'video', name: 'Video', onClick: handleVideoClick } : null,
    ...active.map((a) => ({ id: a.key, name: a.label })),
  ].filter(Boolean);
  if (!attributeItems.length) return <Dash />;

  return (
    <OverflowNameList
      items={attributeItems}
      idPrefix='exercise-attributes'
      rowId={e.id}
      variant='pill'
      maxVisible={attributeItems.length}
      adaptiveWrap={false}
    />
  );
};

AttributesCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      type: PropTypes.string,
      inputType: PropTypes.string,
      unit: PropTypes.string,
      detail: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      detailType: PropTypes.string,
      defaultOptions: PropTypes.shape({}),
      videoUrl: PropTypes.string,
      onPlayVideo: PropTypes.func,
    }),
  }).isRequired,
};

const SourceProgramCell = ({ row }) => {
  const { sourceProgram } = row.original;
  return sourceProgram
    ? <ExerciseNameText title={sourceProgram}>{sourceProgram}</ExerciseNameText>
    : <Dash />;
};

SourceProgramCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({ sourceProgram: PropTypes.string }),
  }).isRequired,
};

// `createdAt` is a Unix timestamp in seconds (serializer contract); Date wants ms.
const formatAdded = (addedAt) => {
  if (!addedAt) return null;
  const d = new Date(Number(addedAt) * 1000);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const AddedText = styled.span`
  white-space: nowrap;
`;

const AddedCell = ({ row }) => {
  const label = formatAdded(row.original.createdAt);
  return label ? <AddedText>{label}</AddedText> : <Dash />;
};

AddedCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      createdAt: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  }).isRequired,
};

const ExerciseActionsMenu = ({ exercise: e }) => {
  const {
    isOpen, menuStyle, menuRef, buttonRef, openMenu, close,
  } = useActionMenu();
  // Linked/synced-in exercises belong to a parent account and can't be edited,
  // deleted, moved or merged — mirror legacy PHP and show a lock, no menu.
  if (e.isReadOnly) {
    return (
      <ActionMenuWrapper title='Linked from another account — read-only'>
        <Icon icon='lock' size={15} color='#BDBDBD' />
      </ActionMenuWrapper>
    );
  }
  return (
    <ActionMenuWrapper onClick={(ev) => ev.stopPropagation()}>
      <ActionMenuButton
        ref={buttonRef}
        type='button'
        onClick={openMenu}
        aria-label='Row actions'
        aria-haspopup='menu'
        aria-expanded={isOpen}
      >
        <Icon icon='dots' size={16} color='currentColor' />
      </ActionMenuButton>
      {isOpen && createPortal(
        <ActionMenuContainer ref={menuRef} style={menuStyle} role='menu'>
          <ActionMenuItemButton type='button' role='menuitem' onClick={() => { close(); e.onEdit(e); }}>
            <Icon icon='edit-workout' size={16} color='#1A1A1A' />
            Edit
          </ActionMenuItemButton>
          {e.isImported && (
            <ActionMenuItemButton type='button' role='menuitem' onClick={() => { close(); e.onMove(e); }}>
              <Icon icon='add-program' size={16} color='#1A1A1A' />
              {e.type === EXERCISE_TYPE.Progression
                ? 'Move to Progressions'
                : 'Move to Our Exercises'}
            </ActionMenuItemButton>
          )}
          <ActionMenuItemButton type='button' role='menuitem' onClick={() => { close(); e.onMerge(e); }}>
            <Icon icon='swap-alt' size={16} color='#1A1A1A' />
            Merge
          </ActionMenuItemButton>
          <ActionMenuDivider />
          <ActionMenuItemButton type='button' role='menuitem' onClick={() => { close(); e.onDelete(e); }}>
            <Icon icon='delete' size={16} color='#FF0505' />
            Delete
          </ActionMenuItemButton>
        </ActionMenuContainer>,
        document.body,
      )}
    </ActionMenuWrapper>
  );
};

ExerciseActionsMenu.propTypes = {
  exercise: PropTypes.shape({
    isImported: PropTypes.bool,
    isReadOnly: PropTypes.bool,
    type: PropTypes.string,
    onEdit: PropTypes.func,
    onMove: PropTypes.func,
    onMerge: PropTypes.func,
    onDelete: PropTypes.func,
  }).isRequired,
};

const ExerciseActionsCell = ({ row }) => <ExerciseActionsMenu exercise={row.original} />;

ExerciseActionsCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.shape({}) }).isRequired,
};

// Tokenized over the merged name+tags string: every whitespace-separated term
// must hit somewhere, so "test lower" matches a row tagged Test + Lower Body.
const exerciseSearch = (row, q) => {
  const haystack = [row.name, ...(row.tags || []).map((t) => t.name)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return q.split(/\s+/).every((term) => haystack.includes(term));
};

const ManageExercisesTable = React.forwardRef(({
  exercises, isLoading, hasError, onRetry, emptyMessage, activeTab,
  onEditExercise, onMoveExercise, onMergeExercise, onDeleteExercise, onPlayVideo,
  onBulkDelete, onBulkMove, clearSelectionToken, filterControl, bannerContent,
  searchControl, searchQuery, onSearchQueryChange,
}, ref) => {
  const innerRef = useRef(null);

  // Container calls scrollToExercise after a merge; forward it to the shared
  // table's scroll-to-row (which also pulses the row).
  useImperativeHandle(ref, () => ({
    scrollToExercise: (id) => innerRef.current?.scrollToRow(id),
  }), []);

  const isImported = activeTab === 'imported';

  // Linked (read-only) exercises can't be bulk-selected. Stable identity so the
  // shared table's selection memos don't recompute every render.
  const isRowSelectable = useCallback((row) => !row.isReadOnly, []);

  const tableData = useMemo(
    () => (exercises || []).map((e) => ({
      ...e,
      isImported,
      onEdit: onEditExercise,
      onMove: onMoveExercise,
      onMerge: onMergeExercise,
      onDelete: onDeleteExercise,
      onPlayVideo,
    })),
    [exercises, isImported, onEditExercise, onMoveExercise, onMergeExercise,
      onDeleteExercise, onPlayVideo],
  );

  const columns = useMemo(() => [
    {
      Header: 'Exercise',
      accessor: 'name',
      sortType: nameSort,
      headerStyle: { minWidth: isImported ? '230px' : '240px', flex: '1' },
      cellStyle: { minWidth: isImported ? '230px' : '240px', flex: '1' },
      Cell: NameCell,
    },
    {
      Header: 'Type',
      id: 'type',
      accessor: (row) => TYPE_LABELS[row.type] || '',
      sortType: nameSort,
      headerStyle: { width: '120px' },
      cellStyle: { width: '120px' },
      Cell: TypeCell,
    },
    {
      Header: 'Tracking',
      id: 'tracking',
      accessor: getTrackingLabel,
      disableSortBy: true,
      headerStyle: { width: isImported ? '120px' : '140px' },
      cellStyle: { width: isImported ? '120px' : '140px' },
      Cell: TrackingCell,
    },
    ...(isImported ? [
      {
        Header: 'Tags',
        id: 'tags',
        accessor: (row) => (row.tags || []).map((t) => t.name).join(' '),
        disableSortBy: true,
        headerStyle: { width: '150px' },
        cellStyle: { width: '150px' },
        Cell: TagsCell,
      },
      {
        Header: 'Attributes',
        id: 'attributes',
        disableSortBy: true,
        headerStyle: { width: '190px' },
        cellStyle: { width: '190px' },
        Cell: AttributesCell,
      },
      {
        Header: 'Imported from',
        id: 'sourceProgram',
        accessor: (row) => row.sourceProgram || '',
        sortType: nameSort,
        headerStyle: { width: '140px' },
        cellStyle: { width: '140px' },
        Cell: SourceProgramCell,
      },
      {
        Header: 'Added',
        id: 'createdAt',
        accessor: (row) => row.createdAt || '',
        headerStyle: { width: '120px' },
        cellStyle: { width: '120px' },
        Cell: AddedCell,
      },
    ] : [
      {
        Header: 'Linked Exercise',
        id: 'linkedExercise',
        disableSortBy: true,
        headerStyle: { width: '180px' },
        cellStyle: { width: '180px' },
        Cell: LinkedExerciseCell,
      },
      {
        Header: 'Tags',
        id: 'tags',
        accessor: (row) => (row.tags || []).map((t) => t.name).join(' '),
        disableSortBy: true,
        headerStyle: { width: '180px' },
        cellStyle: { width: '180px' },
        Cell: TagsCell,
      },
      {
        Header: 'Attributes',
        id: 'attributes',
        disableSortBy: true,
        headerStyle: { width: '280px' },
        cellStyle: { width: '280px' },
        Cell: AttributesCell,
      },
    ]),
    {
      Header: 'Actions',
      id: 'actions',
      sticky: 'right',
      disableSortBy: true,
      headerStyle: { width: '90px', textAlign: 'center' },
      cellStyle: { width: '90px', textAlign: 'center' },
      Cell: ExerciseActionsCell,
    },
  ], [isImported]);

  const bulkActions = useMemo(() => (isImported ? [
    {
      key: 'moveToMaster',
      label: (selectedIds) => getMoveLabel(
        selectedIds.map((id) => exercises.find((e) => e.id === id)?.type),
      ),
      icon: 'add-program',
      iconColor: '#444444',
    },
    {
      key: 'deleteExercises', label: 'Delete', icon: 'delete', iconColor: '#FF0505', dividerBefore: true,
    },
  ] : [
    {
      key: 'deleteExercises', label: 'Delete', icon: 'delete', iconColor: '#FF0505',
    },
  ]), [isImported, exercises]);

  const handleBulkAction = (key, selectedIds) => {
    if (key === 'moveToMaster') onBulkMove(selectedIds);
    else if (key === 'deleteExercises') onBulkDelete(selectedIds);
  };

  return (
    <NavigatorManageTable
      ref={innerRef}
      data={tableData}
      columns={columns}
      isLoading={isLoading}
      hasError={hasError}
      errorMessage='Something went wrong loading exercises.'
      onRetry={onRetry}
      searchFn={exerciseSearch}
      searchPlaceholder='Search for an exercise'
      emptyMessage={emptyMessage}
      searchEmptyMessage='No exercises match your search.'
      entityLabel='exercise'
      defaultSortBy={[{ id: 'name', desc: false }]}
      isRowSelectable={isRowSelectable}
      bulkActions={bulkActions}
      onBulkAction={handleBulkAction}
      extraActionContent={filterControl}
      bannerContent={bannerContent}
      searchControl={searchControl}
      searchQuery={searchQuery}
      onSearchQueryChange={onSearchQueryChange}
      clearSelectionToken={clearSelectionToken}
      virtualizeRows
      variableRowHeight
    />
  );
});

ManageExercisesTable.displayName = 'ManageExercisesTable';

ManageExercisesTable.propTypes = {
  exercises: PropTypes.arrayOf(PropTypes.shape({})),
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool,
  onRetry: PropTypes.func,
  emptyMessage: PropTypes.string,
  activeTab: PropTypes.string,
  onEditExercise: PropTypes.func,
  onMoveExercise: PropTypes.func,
  onMergeExercise: PropTypes.func,
  onDeleteExercise: PropTypes.func,
  onPlayVideo: PropTypes.func,
  onBulkDelete: PropTypes.func,
  onBulkMove: PropTypes.func,
  clearSelectionToken: PropTypes.number,
  filterControl: PropTypes.node,
  bannerContent: PropTypes.node,
  searchControl: PropTypes.node,
  searchQuery: PropTypes.string,
  onSearchQueryChange: PropTypes.func,
};

ManageExercisesTable.defaultProps = {
  exercises: [],
  isLoading: false,
  hasError: false,
  onRetry: () => {},
  emptyMessage: 'No exercises found.',
  activeTab: 'master',
  onEditExercise: () => {},
  onMoveExercise: () => {},
  onMergeExercise: () => {},
  onDeleteExercise: () => {},
  onPlayVideo: () => {},
  onBulkDelete: () => {},
  onBulkMove: () => {},
  clearSelectionToken: 0,
  filterControl: null,
  bannerContent: null,
  searchControl: null,
  searchQuery: undefined,
  onSearchQueryChange: () => {},
};

export default ManageExercisesTable;
